import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogBody, DialogFooter, Button, Textarea,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import type { ServiceConfigOptionPrice } from '@/api/services';
import {
  useCreateConfigOptionPrice,
  useUpdateConfigOptionPrice,
  type ConfigOptionPricePayload,
} from '@/api/services-mutations';
import { DateInput } from '@/components/date-input';
import { NumericInput } from '@/components/numeric-input';

function errMsg(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong.';
}

const today = () => new Date().toISOString().slice(0, 10);

type FormState = { commitment_value: string; commitment_price_per_unit: string; effective_from: string; effective_to: string; description: string };
const empty = (): FormState => ({ commitment_value: '0', commitment_price_per_unit: '0', effective_from: today(), effective_to: '', description: '' });

export interface ConfigPriceDialogProps {
  serviceId: number;
  configId: number;
  configName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set the dialog edits this row; otherwise it creates a new one. */
  price?: ServiceConfigOptionPrice | null;
}

/** Add / edit dialog for a config option's committed-price row (BundleConfigPrices). */
export function ConfigPriceDialog({ serviceId, configId, configName, open, onOpenChange, price }: ConfigPriceDialogProps) {
  const toast = useToast();
  const isEdit = !!price;
  const [form, setForm] = useState<FormState>(empty);
  const create = useCreateConfigOptionPrice(serviceId, configId);
  const update = useUpdateConfigOptionPrice(serviceId, configId);
  const mutation = isEdit ? update : create;

  useEffect(() => {
    if (!open) return;
    setForm(price
      ? {
          commitment_value: String(price.commitment_value ?? 0),
          commitment_price_per_unit: String(price.commitment_price_per_unit ?? 0),
          effective_from: price.effective_from ?? today(),
          effective_to: price.effective_to ?? '',
          description: price.description ?? '',
        }
      : empty());
    create.reset();
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, price]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));
  const fromInvalid = form.effective_from.trim() === '';

  const close = (next: boolean) => { if (!mutation.isPending) onOpenChange(next); };

  const submit = () => {
    if (fromInvalid) return;
    const data: ConfigOptionPricePayload = {
      commitment_value: Number(form.commitment_value) || 0,
      commitment_price_per_unit: Number(form.commitment_price_per_unit) || 0,
      effective_from: form.effective_from,
      effective_to: form.effective_to.trim() ? form.effective_to : null,
      description: form.description.trim() || undefined,
    };
    const onSuccess = () => { toast.success(isEdit ? 'Price updated' : 'Price added'); close(false); };
    if (isEdit && price) update.mutate({ priceId: price.id, data }, { onSuccess });
    else create.mutate(data, { onSuccess });
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit price' : 'Add price'}</DialogTitle>
          <DialogDescription>Committed price for “{configName}”.</DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {mutation.isError ? <Alert tone="danger" title="Could not save price">{errMsg(mutation.error)}</Alert> : null}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
            <Field label="Commitment value">
              {(p) => <NumericInput {...p} value={form.commitment_value} onChange={(e) => set('commitment_value', e.target.value)} />}
            </Field>
            <Field label="Unit price">
              {(p) => <NumericInput {...p} value={form.commitment_price_per_unit} onChange={(e) => set('commitment_price_per_unit', e.target.value)} />}
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
            <Field label="Effective from" required error={fromInvalid ? 'Required' : undefined}>
              {(p) => <DateInput {...p} value={form.effective_from} invalid={fromInvalid} onChange={(e) => set('effective_from', e.target.value)} />}
            </Field>
            <Field label="Effective to" description="Blank = ongoing.">
              {(p) => <DateInput {...p} value={form.effective_to} onChange={(e) => set('effective_to', e.target.value)} />}
            </Field>
          </div>
          <Field label="Description">
            {(p) => <Textarea {...p} rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} />}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" disabled={mutation.isPending} onClick={() => close(false)}>Cancel</Button>
          <Button variant="primary" loading={mutation.isPending} disabled={fromInvalid} onClick={submit}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
